import { NAVIGATOR_LEAD_ENDPOINT, NAVIGATOR_LEAD_SOURCE } from '../../config/navigatorLeadConfig'
import type { NavigatorLeadPayload } from '../../data/regulations/types'

export type LeadSubmitResult = { ok: true } | { ok: false; error: string; configured: boolean }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function validateNavigatorLead(payload: NavigatorLeadPayload): string[] {
  const errors: string[] = []
  if (!EMAIL.test(payload.workEmail.trim())) errors.push('Enter a valid work email.')
  if (!payload.companyName.trim()) errors.push('Company name is required.')
  if (!payload.consentProcessing) errors.push('Consent to the stated processing purpose is required.')
  return errors
}

/** Fields that may leave the browser. Questionnaire answers are intentionally absent. */
export function navigatorLeadBody(payload: NavigatorLeadPayload): Record<string, string> {
  return {
    email: payload.workEmail.trim(),
    workEmail: payload.workEmail.trim(),
    company: payload.companyName.trim(),
    companyName: payload.companyName.trim(),
    fullName: (payload.name || '').trim(),
    firstName: (payload.name || '').trim(),
    jobTitle: (payload.jobTitle || '').trim(),
    phone: (payload.phone || '').trim(),
    sector: (payload.sector || '').trim(),
    interestCarbonAccounting: payload.interestCarbonAccounting ? 'yes' : 'no',
    interestReportingDemo: payload.interestReportingDemo ? 'yes' : 'no',
    consentProcessing: payload.consentProcessing ? 'yes' : 'no',
    consentMarketing: payload.consentMarketing ? 'yes' : 'no',
    checklistCount: String(payload.checklistCount ?? ''),
    source: NAVIGATOR_LEAD_SOURCE,
    formType: 'navigator-lead',
    page: '/uae-sustainability-regulations/checklist',
  }
}

export async function submitNavigatorLead(
  payload: NavigatorLeadPayload,
  options: { endpoint?: string; fetchImpl?: typeof fetch } = {},
): Promise<LeadSubmitResult> {
  const errors = validateNavigatorLead(payload)
  if (errors.length) return { ok: false, error: errors[0], configured: true }

  const endpoint = options.endpoint ?? NAVIGATOR_LEAD_ENDPOINT
  if (!endpoint) {
    return {
      ok: false,
      configured: false,
      error: 'Automated lead submission is not configured. You can still print or download the checklist.',
    }
  }

  try {
    const fetchImpl = options.fetchImpl || fetch
    const response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(navigatorLeadBody(payload)),
    })
    const data = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string }
    if (!response.ok || data.ok === false) {
      const unconfigured = response.status === 503 || response.status === 500 || /not configured/i.test(data.error || '')
      return {
        ok: false,
        configured: !unconfigured,
        error: data.error || 'The form was not accepted. Your checklist is still available to print or download.',
      }
    }
    return { ok: true }
  } catch {
    return {
      ok: false,
      configured: true,
      error: 'Network error. Your checklist was not lost. Print or download it and try again later.',
    }
  }
}
